import { useQuery } from "@tanstack/react-query";
import { PLAN_DOC_FOLDER, PLAN_DOC_SLUG, type ProjectDetailDTO } from "@app-factory/shared";
import { api } from "@/lib/api";
import { cs } from "@/i18n/cs";
import { EmptyState, Panel } from "@/design/primitives";
import { relativeTime } from "@/lib/format";
import { useKnowledgeTree } from "../project-data";

interface PlanItem {
  text: string;
  done: boolean;
}

interface PlanSection {
  title: string;
  items: PlanItem[];
}

/**
 * Markdown checklist → sekce s položkami. Bere jen řádky `- [ ]` / `- [x]`;
 * nadpisy (#, ##, ###) tvoří sekce, sekce bez položek se zahazují (typicky
 * úvodní titulek dokumentu a doprovodný text).
 */
function parsePlan(content: string): { sections: PlanSection[]; done: number; total: number } {
  const sections: PlanSection[] = [];
  let current: PlanSection = { title: "", items: [] };
  for (const raw of content.split("\n")) {
    const line = raw.trim();
    const heading = /^#{1,3}\s+(.+)$/.exec(line);
    if (heading) {
      if (current.items.length > 0) sections.push(current);
      current = { title: heading[1] ?? "", items: [] };
      continue;
    }
    const item = /^[-*]\s+\[([ xX])\]\s+(.+)$/.exec(line);
    if (item) current.items.push({ done: item[1] !== " ", text: item[2] ?? "" });
  }
  if (current.items.length > 0) sections.push(current);
  const total = sections.reduce((n, s) => n + s.items.length, 0);
  const done = sections.reduce((n, s) => n + s.items.filter((i) => i.done).length, 0);
  return { sections, done, total };
}

/** PLÁN VÝVOJE — checklist derivovaný z Knowledge (Product/plan-vyvoje), udržuje AI. */
export function PlanTab({ project }: { project: ProjectDetailDTO }) {
  const tree = useKnowledgeTree(project.id);
  const planMeta = (tree.data?.folders ?? [])
    .find((f) => f.folder === PLAN_DOC_FOLDER)
    ?.docs.find((d) => d.slug === PLAN_DOC_SLUG && d.status === "ACTIVE");

  const doc = useQuery({
    queryKey: ["knowledge-doc", planMeta?.id],
    queryFn: () => api.knowledgeDoc(planMeta!.id),
    enabled: !!planMeta,
  });

  if (tree.isLoading) return null;

  if (!planMeta) {
    return (
      <div className="mt-4">
        <EmptyState title={cs.devPlan.emptyTitle} note={cs.devPlan.emptyBody} />
      </div>
    );
  }
  if (!doc.data) return null;

  const { sections, done, total } = parsePlan(doc.data.doc.content);
  const pct = total > 0 ? Math.round((done / total) * 100) : 0;

  return (
    <div className="mt-4" data-testid="plan-tab">
      <Panel className="p-4">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div>
            <div className="af-label" style={{ color: "var(--accent)" }}>
              {cs.devPlan.header}
            </div>
            <div className="mt-1 text-[11px]" style={{ color: "var(--text-tertiary)" }}>
              {cs.devPlan.note} · {cs.devPlan.updated} {relativeTime(planMeta.updatedAt)}
            </div>
          </div>
          <div className="min-w-[180px] flex-1 sm:max-w-[320px]">
            <div className="flex justify-between text-[11px]" style={{ color: "var(--text-secondary)" }}>
              <span>{cs.devPlan.progress(done, total)}</span>
              <span className="af-display font-bold" style={{ color: pct === 100 ? "var(--positive)" : "var(--accent)" }}>
                {pct}%
              </span>
            </div>
            <div className="mt-1 h-[6px] w-full" style={{ background: "color-mix(in srgb, var(--accent) 8%, transparent)" }} data-testid="plan-progress">
              <div
                className="h-full"
                style={{
                  width: `${pct}%`,
                  background: pct === 100 ? "var(--positive)" : "var(--accent)",
                }}
              />
            </div>
          </div>
        </div>
      </Panel>

      {sections.length === 0 ? (
        <Panel className="mt-3 p-4">
          <div className="text-[11px]" style={{ color: "var(--text-tertiary)" }}>
            {cs.devPlan.noChecklist}
          </div>
          <pre
            className="mt-2 whitespace-pre-wrap text-[12px] leading-[1.7]"
            style={{ color: "var(--text-primary)", fontFamily: "inherit" }}
          >
            {doc.data.doc.content}
          </pre>
        </Panel>
      ) : (
        <div className="mt-3 grid grid-cols-1 gap-3 xl:grid-cols-2">
          {sections.map((s, si) => {
            const sectionDone = s.items.filter((i) => i.done).length;
            return (
              <Panel key={`${s.title}-${si}`} className="p-4" data-testid="plan-section">
                <div className="flex items-baseline justify-between gap-3">
                  <div className="text-[12px] font-semibold tracking-[0.06em]" style={{ color: "var(--text-primary)" }}>
                    {s.title || cs.devPlan.untitledSection}
                  </div>
                  <div className="flex-none text-[11px]" style={{ color: sectionDone === s.items.length ? "var(--positive)" : "var(--text-tertiary)" }}>
                    {sectionDone}/{s.items.length}
                  </div>
                </div>
                <div className="mt-2 flex flex-col gap-1">
                  {s.items.map((item, ii) => (
                    <div key={ii} className="flex gap-2 text-[11px] leading-[1.6]" data-testid="plan-item">
                      <span
                        className="flex-none"
                        style={{ color: item.done ? "var(--positive)" : "var(--accent)" }}
                      >
                        {item.done ? "✓" : "▢"}
                      </span>
                      <span
                        className="min-w-0 break-words"
                        style={{
                          color: item.done ? "var(--text-tertiary)" : "var(--text-secondary)",
                          textDecoration: item.done ? "line-through" : undefined,
                        }}
                      >
                        {item.text}
                      </span>
                    </div>
                  ))}
                </div>
              </Panel>
            );
          })}
        </div>
      )}
    </div>
  );
}
